<template>
  <section v-if="recentProducts.length > 0" class="recently_view_sec">
    <div class="recently-title">
      <h2 class="card_heading scroll-trigger animate--slide-in">Recently Viewed</h2>
    </div>

    <swiper
      :modules="modules"
      :space-between="10"
      :speed="1200"
      :navigation="{
        prevEl: '.recently-viewed-product-nav-button-prev',
        nextEl: '.recently-viewed-product-nav-button-next',
        disabledClass: 'swiper-button-disabled',
      }"
      :breakpoints="{
        300: {
          slidesPerView: 2,
          slidesPerGroup: 1,
        },
        575: {
          slidesPerView: 3,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 3,
          slidesPerGroup: 1,
        },
        1024: {
          slidesPerView: 4,
          slidesPerGroup: 1,
        },
        1440: {
          slidesPerView: 7,
          slidesPerGroup: 1,
        },
      }"
      class="recentViewSwiper"
      @swiper="onSwiperInit"
      @resize="onSwiperResize"
    >
      <swiper-slide v-for="(item, index) in recentProducts" :key="index" class="recent-slide">
        <div class="c-product recently-viewed-product">
          <div class="c-product__img recently-viewed-product-img">
            <a :href="item.productUrl" :aria-label="item.productTitle + ' Product Link'">
              <img
                :src="item.productImg"
                :alt="item.productTitle + ' Product Image'"
                :aria-label="item.productTitle + ' Product Image'"
                loading="lazy"
                aria-hidden="true"
                width=""
                height=""
              />
            </a>
          </div>

          <h3 class="c-product__title recently-viewed-product-title">
            <a
              class="c-product__url recently-viewed-product-title-link"
              :href="item.productUrl"
              :aria-label="item.productTitle + ' Product Link'"
            >
              {{ item.productTitle }}
            </a>
          </h3>

          <div class="card-title bold product_price recently-viewed-product-price">
            <div class="recently-viewed-product-price-container">
              <span
                v-if="item.productComparePrice != 0"
                class="c-productPrice tmbCamparePrice recently-viewed-product-compare-price"
              >
                <del>{{ currencySymbol }} {{ formatPrice(item.productComparePrice) }}</del>
              </span>

              <span class="c-productPrice tmb_discounted recently-viewed-product-price">
                {{ currencySymbol }} {{ formatPrice(item.productPrice) }}
              </span>

              <span
                v-if="item.productComparePrice != 0"
                class="c-productPrice tmbDicountPercentage recently-viewed-product-discount-percentage"
              >
                {{ item.discountPercentage }}% Off
              </span>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <!-- Custom Navigation Buttons -->
    <div
      v-if="canSlide"
      class="recently-viewed-product-nav-button recently-viewed-product-nav-button-prev"
    >
      <span class="recently-viewed-product-nav-button-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="18"
          viewBox="0 0 10 18"
          fill="none"
        >
          <path
            d="M9.42871 0.427734L0.857283 8.99916L9.42871 17.5706"
            stroke="black"
            stroke-width="0.75"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </div>

    <div
      v-if="canSlide"
      class="recently-viewed-product-nav-button recently-viewed-product-nav-button-next"
    >
      <span class="recently-viewed-product-nav-button-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="18"
          viewBox="0 0 10 18"
          fill="none"
        >
          <path
            d="M9.42871 0.427734L0.857283 8.99916L9.42871 17.5706"
            stroke="black"
            stroke-width="0.75"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </div>
  </section>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import SwiperCore, { Navigation } from 'swiper';
import { currencyJson } from '@/currency';

SwiperCore.use([Navigation]);

import 'swiper/css';
import 'swiper/css/navigation';

export default {
  name: 'RenderRecentlyViewed',
  components: { Swiper, SwiperSlide },
  props: {
    shopifyData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      recentProducts: [],
      maxProducts: 20,
      slidesPerView: 3,
      modules: [Navigation],
      currencySymbol: currencyJson[window?.Shopify?.currency?.active]?.symbol_native || '$',
      canSlide: false,
      swiperInstance: null,
    };
  },
  watch: {
    shopifyData: {
      handler(newVal) {
        if (newVal && newVal.title) {
          this.setRecentlyViewed();
          this.getRecentlyViewed();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.getRecentlyViewed();
    this.setRecentlyViewed();
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    onSwiperInit(swiper) {
      this.swiperInstance = swiper;
      this.checkIfCanSlide();
    },

    onSwiperResize() {
      this.checkIfCanSlide();
    },

    checkIfCanSlide() {
      if (!this.swiperInstance) return;

      const swiper = this.swiperInstance;
      const maxTranslate = Math.abs(swiper.maxTranslate());

      this.onSwiperReady = true;
      this.canSlide = maxTranslate > 0;

      this.$nextTick(() => {
        if (this.canSlide && swiper.navigation) {
          swiper.navigation.destroy();
          swiper.navigation.init();
          swiper.navigation.update();
        }
      });
    },

    handleResize() {
      if (window.innerWidth < 640) this.slidesPerView = 1.5;
      else if (window.innerWidth < 1024) this.slidesPerView = 2;
      else this.slidesPerView = 3;
    },

    // ✅ Currency formatting helper
    formatPrice(value) {
      if (!value) return '0';
      const num = parseFloat(value);
      return num.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    // set recently viewed products to local storage
    setRecentlyViewed() {
      const product = this.shopifyData;
      if (!product || !product.title) return;

      const pdpData = {
        productTitle: product.title,
        productImg: product.featured_image || '',
        productPrice: product.price || 0,
        productComparePrice:
          product.compare_at_price > product.price && product.compare_at_price != 0
            ? product.compare_at_price
            : 0,
        discountPercentage:
          product.compare_at_price > 0
            ? Math.round(
                ((product.compare_at_price - product.price) / product.compare_at_price) * 100
              )
            : 0,
        productUrl: product.url || '',
      };

      const localData = localStorage.getItem('recently_viewed');
      const currPdpTitle = pdpData.productTitle;

      if (!localData) {
        localStorage.setItem('recently_viewed', JSON.stringify([pdpData]));
      } else {
        const oldPdpData = JSON.parse(localData);
        const reVisitPdp = oldPdpData.some((p) => p.productTitle === currPdpTitle);

        if (!reVisitPdp) {
          if (oldPdpData.length >= this.maxProducts) oldPdpData.shift();
          oldPdpData.push(pdpData);
          localStorage.setItem('recently_viewed', JSON.stringify(oldPdpData));
        }
      }
    },

    // get recently viewed products from local storage
    getRecentlyViewed() {
      const localPdpData = JSON.parse(localStorage.getItem('recently_viewed')) || [];
      this.recentProducts = localPdpData.reverse();
    },
  },
};
</script>

<style scoped>
.recently_view_sec {
  margin-top: 30px;
  position: relative;
  padding-left: 15px;
  padding-bottom: 15px;
}
.card_heading {
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin: 0px 0 8px 0px;
}

.swiper-wrapper {
  transition-timing-function: ease;
}
.recently-viewed-product-img img {
  width: 100%;
}
.recently-viewed-product-title {
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
}
.recently-viewed-product-price-container {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 6px;
}
.recently-viewed-product-compare-price del {
  color: #999;
  font-size: 12px;
}
.recently-viewed-product-price {
  color: #000;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.recently-viewed-product-discount-percentage {
  color: #d32f2f;
  font-weight: 600;
  font-size: 12px;
}
.recent-slide {
  width: auto; /* for slides-per-view: auto */
}
/* Hide default Swiper navigation buttons */
.swiper-button-prev:not(.custom-swiper-button-prev),
.swiper-button-next:not(.custom-swiper-button-next) {
  display: none;
}

/* Custom navigation buttons */
.recently-viewed-product-nav-button {
  color: #0000005b;
  background: #fff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  z-index: 10;
  cursor: pointer;
  transform: translateY(-50%);
}
.recently-viewed-product-nav-button-prev {
  left: 15px;
}
.recently-viewed-product-nav-button-next {
  right: 15px;
  transform: translateY(-50%) rotate(180deg);
}
.recently-viewed-product-nav-button-icon {
  font-size: 18px;
  line-height: 1;
}
@media screen and (max-width: 991px) {
  .recently_view_sec {
    padding-left: 0;
    padding-bottom: 0;
  }
  .recently-title .card_heading {
    margin: 0px 0 8px 15px;
  }
}
@media screen and (max-width: 479px) {
  .recently-viewed-product-nav-button {
    display: none;
  }
}
</style>
